import classNames from "classnames";
import { memo, useMemo, useRef } from "react";
import { Column as ReactTableColumn, useTable } from "react-table";
import { AutoSizer, Column, InfiniteLoader, Table } from "react-virtualized";
import { Button, Loader, Segment } from "semantic-ui-react";
import {
  GetNotifyNotificationContentSearchResponse,
  GetNotifyNotificationStatus,
} from "../../../../../../../../queries/notify";
import { useInfiniteNotificationContent } from "../../../../../../hooks/useInfiniteNotificationContent";
import { useContentHeight } from "../../../hooks/useContentHeight";

import { faFileCertificate, faPen } from "@fortawesome/pro-regular-svg-icons";
import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons";
import AccessibleCheckbox from "../../../../../../../../components/global_components/AccessibleCheckbox";
import { useClient } from "../../../../../../../../hooks/use-client";
import useSite from "../../../../../../../permits/components/inspections/InspectionPanel/useSite";
import { useBulkSelect } from "../../../../../../hooks/useBulkSelect";
import { ChannelIconList } from "./NotifyNotificationsChannelIcon";
import { NotifyNotificationsDates } from "./NotifyNotificationsDates";
import { NotifyNotificationsMetaStat, NotifyNotificationsMetaStatType } from "./NotifyNotificationsMetaStat";
import { NotifyNotificationsStatusLabel } from "./NotifyNotificationsStatusLabel";
import "./NotifyNotificationsTableInfiniteList.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useScrollFadeHorizontal } from "../../../hooks/useScrollFadeHorizontal";

export const NotifyNotificationsTableInfiniteList = memo(
  ({
    titleBarRef,
    filters,
  }: {
    titleBarRef: React.RefObject<HTMLDivElement>;
    filters: {
      query: string;
      subscriptionGroupIds: number[];
      departmentGroupId: number | null;
      status: GetNotifyNotificationStatus | null;
    };
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const site = useSite();
    const { name: siteName } = useClient(site);

    const titleBarHeight = useContentHeight(titleBarRef, {
      defaultFudge: 18,
      mobileFudge: 24,
    });
    const { containerClassNames } = useScrollFadeHorizontal(containerRef);

    const {
      notifyContent,
      total,
      isNotificationContentFetching,
      fetchMoreNotificationContent,
      canFetchMoreNotificationContent,
    } = useInfiniteNotificationContent({
      take: 50,
      query: filters.query,
      subscriptionGroupIds: filters.subscriptionGroupIds,
      departmentGroupId: filters.departmentGroupId,
      status: filters.status,
    });

    const loadMoreRows = async () => {
      if (!canFetchMoreNotificationContent || isNotificationContentFetching) return;
      await fetchMoreNotificationContent();
    };

    const {
      bulkSelections,
      onBulkSelectAll,
      shouldBulkSelectBeChecked,
      onBulkSelect,
      shouldBulkSelectAllBeChecked,
      shouldBulkSelectAllBeIndeterminate,
      selectedCount,
    } = useBulkSelect({
      totalCount: total,
    });

    const columns = useMemo<ReactTableColumn<GetNotifyNotificationContentSearchResponse["data"][number]>[]>(
      () => [
        {
          id: "selectAndImage",
          Header: () => {
            return (
              <AccessibleCheckbox
                onChange={onBulkSelectAll}
                checked={shouldBulkSelectAllBeChecked()}
                indeterminate={shouldBulkSelectAllBeIndeterminate()}
              />
            );
          },
          Cell: ({ row: { original } }) => {
            return (
              <div className='tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center'>
                <div
                  className={classNames("row-checkbox tw-absolute tw-left-3 tw-top-3 tw-cursor-pointer", {
                    "tw-hidden": selectedCount === 0,
                  })}
                >
                  <AccessibleCheckbox
                    checked={shouldBulkSelectBeChecked(original.id)}
                    onChange={() => {
                      onBulkSelect(original.id);
                    }}
                  />
                </div>
                {original.mainImageUrl && (
                  <img
                    src={original.mainImageUrl}
                    alt='first in document'
                    className='tw-h-[72px] tw-w-[72px] tw-rounded-sm tw-object-cover'
                  />
                )}
              </div>
            );
          },
          minWidth: 100,
          maxWidth: 100,
        },
        {
          id: "name",
          Header: "Name",
          Cell: ({ row: { original } }) => {
            return (
              <div className='tw-flex tw-flex-col tw-gap-1'>
                <span className='tw-text-base tw-font-semibold tw-text-text-hint'>
                  From {original.departmentSentFrom?.name ?? siteName}
                </span>
                <span className='tw-line-clamp-1 tw-text-lg tw-font-bold'>{original.headline}</span>
                <span className='tw-line-clamp-1 tw-text-base tw-font-bold'>{original.tagline}</span>
              </div>
            );
          },
          width: 350,
        },
        {
          id: "channels",
          Header: "Channel",
          Cell: ({ row: { original } }) => {
            const shouldShowStats = true;
            return (
              <div className='tw-flex tw-flex-col tw-gap-1'>
                <ChannelIconList channels={original.channels} />
                {shouldShowStats && (
                  <div className='tw-flex tw-flex-wrap tw-justify-start tw-gap-x-4 tw-gap-y-1'>
                    <NotifyNotificationsMetaStat
                      type={NotifyNotificationsMetaStatType.NotificationsDelivered}
                      value={0}
                    />
                    <NotifyNotificationsMetaStat type={NotifyNotificationsMetaStatType.NotificationsFailed} value={0} />
                    <NotifyNotificationsMetaStat type={NotifyNotificationsMetaStatType.Subscribers} value={0} />
                    <NotifyNotificationsMetaStat type={NotifyNotificationsMetaStatType.Views} value={0} />
                    <NotifyNotificationsMetaStat type={NotifyNotificationsMetaStatType.Likes} value={0} />
                  </div>
                )}
              </div>
            );
          },
          flexGrow: 1,
        },
        {
          id: "status",
          Header: "Status",
          Cell: ({ row: { original } }) => {
            return (
              <div className='tw-flex tw-flex-col tw-gap-1'>
                <NotifyNotificationsStatusLabel type={original.status} />
                <NotifyNotificationsDates content={original} />
              </div>
            );
          },
          flexGrow: 1,
        },
        {
          id: "actions",
          Header: "",
          Cell: ({ row: { original } }) => {
            return (
              <div className='tw-flex tw-justify-end tw-gap-2'>
                {original.status === "Pending" && (
                  <Button primary className='tw-m-0 tw-flex tw-gap-2'>
                    <FontAwesomeIcon icon={faFileCertificate} />
                    Review
                  </Button>
                )}
                {original.status === "Draft" && (
                  <Button primary className='tw-m-0 tw-flex tw-gap-2'>
                    <FontAwesomeIcon icon={faPen} />
                    Edit
                  </Button>
                )}
                <Button className='button-ghost black icon tw-m-0'>
                  <FontAwesomeIcon icon={faEllipsisH} />
                </Button>
              </div>
            );
          },
          // width: 300,
          flexGrow: 1,
        },
      ],
      [bulkSelections],
    );

    const { rows, prepareRow, headers } = useTable({
      columns,
      data: notifyContent,
    });

    const totalWidth = headers.reduce((acc, column) => {
      if (!column.isVisible) return acc;
      if (column.width) return Number(column.width) + acc;
      return column.minWidth ?? acc;
    }, 0);

    if (isNotificationContentFetching && notifyContent.length === 0) {
      return (
        <Segment className='tw-h-[60vh]' basic>
          <Loader active />
        </Segment>
      );
    }

    return (
      <div className={classNames("tw-rounded-md", containerClassNames)}>
        <div
          ref={containerRef}
          className='notify-notifications-infinite-table tw-overflow-x-auto tw-overflow-y-hidden tw-p-0'
          style={{ height: `calc(100vh - ${titleBarHeight}px)` }}
        >
          <InfiniteLoader
            isRowLoaded={({ index }) => !!notifyContent[index]}
            loadMoreRows={loadMoreRows}
            rowCount={total}
            threshold={10}
          >
            {({ onRowsRendered, registerChild }) => (
              <AutoSizer>
                {({ width, height }) => (
                  <Table
                    headerHeight={45}
                    rowCount={rows.length}
                    rowHeight={114}
                    ref={registerChild}
                    width={Math.max(width - 2, totalWidth)} // Magic 2 to account for border
                    height={height}
                    onRowsRendered={onRowsRendered}
                    rowGetter={({ index }) => {
                      const row = rows[index];
                      prepareRow(row);
                      return row;
                    }}
                    style={{ minWidth: `${Math.max(width - 2, totalWidth)}px` }} // Magic 2 to account for border
                    // onRowClick={({ rowData }) => {
                    // }}
                  >
                    {headers
                      .filter((column) => column.isVisible)
                      .map((column) => (
                        <Column
                          key={column.id}
                          dataKey={column.id}
                          maxWidth={column.maxWidth}
                          minWidth={column.minWidth}
                          flexGrow={(column as any).flexGrow}
                          flexShrink={(column as any).flexShrink}
                          width={typeof column.width === "number" ? column.width : 150}
                          headerClassName={classNames({
                            "tw-cursor-pointer": column.canSort,
                          })}
                          headerRenderer={() => column.render("Header")}
                          cellRenderer={({ rowIndex, columnIndex }) => rows[rowIndex].cells[columnIndex].render("Cell")}
                        />
                      ))}
                  </Table>
                )}
              </AutoSizer>
            )}
          </InfiniteLoader>
        </div>
      </div>
    );
  },
);
